/**
 * T155P5 — useAutomationPreferences
 *
 * Preference resolution order:
 * 1) persisted profile preferences (server-backed)
 * 2) localStorage fallback (legacy/backward-compat)
 * 3) safe defaults
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/features/hooks';

const KEYS = {
  autoCategorize: 'fintrack:automation-auto-categorize',
  showSuggestions: 'fintrack:automation-show-suggestions',
  requirePreview: 'fintrack:automation-require-preview',
} as const;

function readBool(key: string, defaultValue: boolean): boolean {
  try {
    const stored = localStorage.getItem(key);
    return stored !== null ? JSON.parse(stored) === true : defaultValue;
  } catch {
    return defaultValue;
  }
}

function writeBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

export interface AutomationPreferences {
  autoCategorize: boolean;
  showSuggestions: boolean;
  requirePreview: boolean;
}

const DEFAULTS: AutomationPreferences = {
  autoCategorize: false,
  showSuggestions: true,
  requirePreview: true,
};

function resolveInitialPreferences(profilePrefs?: Partial<AutomationPreferences>): AutomationPreferences {
  return {
    autoCategorize: profilePrefs?.autoCategorize ?? readBool(KEYS.autoCategorize, DEFAULTS.autoCategorize),
    showSuggestions: profilePrefs?.showSuggestions ?? readBool(KEYS.showSuggestions, DEFAULTS.showSuggestions),
    requirePreview: profilePrefs?.requirePreview ?? readBool(KEYS.requirePreview, DEFAULTS.requirePreview),
  };
}

function writeAll(prefs: AutomationPreferences) {
  writeBool(KEYS.autoCategorize, prefs.autoCategorize);
  writeBool(KEYS.showSuggestions, prefs.showSuggestions);
  writeBool(KEYS.requirePreview, prefs.requirePreview);
}

export function useAutomationPreferences() {
  const profileAutomation = useAppSelector((state) => state.auth.user?.preferences?.automation);

  const [preferences, setPreferences] = useState<AutomationPreferences>(() =>
    resolveInitialPreferences(profileAutomation)
  );

  useEffect(() => {
    if (!profileAutomation) return;
    const next = resolveInitialPreferences(profileAutomation);
    setPreferences(next);
    writeAll(next);
  }, [profileAutomation]);

  const setAutoCategorize = useCallback((value: boolean) => {
    setPreferences((prev) => {
      const next = { ...prev, autoCategorize: value };
      writeAll(next);
      return next;
    });
  }, []);

  const setShowSuggestions = useCallback((value: boolean) => {
    setPreferences((prev) => {
      const next = { ...prev, showSuggestions: value };
      writeAll(next);
      return next;
    });
  }, []);

  const setRequirePreview = useCallback((value: boolean) => {
    setPreferences((prev) => {
      const next = { ...prev, requirePreview: value };
      writeAll(next);
      return next;
    });
  }, []);

  const resetAutomationPreferences = useCallback(() => {
    setPreferences(DEFAULTS);
    writeAll(DEFAULTS);
  }, []);

  return useMemo(() => ({
    autoCategorize: preferences.autoCategorize,
    showSuggestions: preferences.showSuggestions,
    requirePreview: preferences.requirePreview,
    setAutoCategorize,
    setShowSuggestions,
    setRequirePreview,
    resetAutomationPreferences,
    automationPreferences: preferences,
  }), [preferences, resetAutomationPreferences, setAutoCategorize, setRequirePreview, setShowSuggestions]);
}
